import { Component, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { Subscription } from 'rxjs';
import { ObjectStatus } from '../object-status';
import { ApiService } from '../services/api.service';
import { LoadService } from '../services/load.service';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';

@Component({
  selector: 'app-translate',
  templateUrl: './translate.component.html',
  styleUrls: ['./translate.component.css'],
})
export class TranslateComponent implements OnInit {
  // @ts-ignore
  @ViewChild('sharedFilesSort') sharedFilesSort: MatSort;
  // @ts-ignore
  @ViewChild('uploadedFilesSort') uploadedFilesSort: MatSort;
  // @ts-ignore
  @ViewChild('translatedFilesSort') translatedFilesSort: MatSort;

  sharedFiles: ObjectStatus[] = [];
  uploadedFiles: ObjectStatus[] = [];
  translatedFiles: ObjectStatus[] = [];
  selectedFiles: ObjectStatus[] = [];
  targetLanguages = [
    { code: 'ar', fullName: 'Arabic (ar)' },
    { code: 'zh', fullName: 'Chinese (zh)' },
    { code: 'zh-Latn', fullName: 'Chinese (Latin) (zh-Latn)' },
    { code: 'en', fullName: 'English (en)' },
    { code: 'fr', fullName: 'French (fr)' },
    { code: 'de', fullName: 'German (de)' },
    { code: 'hi', fullName: 'Hindi (hi)' },
    { code: 'hi-Latn', fullName: 'Hindi (Latin script) (hi-Latn)' },
    { code: 'id', fullName: 'Indonesian (id)' },
    { code: 'it', fullName: 'Italian (it)' },
    { code: 'ja', fullName: 'Japanese (ja)' },
    { code: 'ja-Latn', fullName: 'Japanese (Latin) (ja-Latn)' },
    { code: 'no', fullName: 'Norwegian (no)' },
    { code: 'pt', fullName: 'Portuguese (pt)' },
    { code: 'ru', fullName: 'Russian (ru)' },
    { code: 'ru-Latn', fullName: 'Russian (Latin) (ru-Latn)' },
    { code: 'es', fullName: 'Spanish (es)' },
  ];

  fileTypes = ['pdf', 'docx', 'pptx', 'html', 'txt', 'xlsx', 'xlf', 'xliff', 'srt'];

  sharedFilesDataSource = new MatTableDataSource<ObjectStatus>();
  uploadedFilesDataSource = new MatTableDataSource<ObjectStatus>();
  translatedFilesDataSource = new MatTableDataSource<ObjectStatus>();

  sharedFilesSubscription: Subscription | null = null;
  uploadedFilesSubscription: Subscription | null = null;
  translatedFilesSubscription: Subscription | null = null;

  selectedLanguage: string = '';
  selectedTargetLanguage: string = '';

  flag_loader_shared = true;
  flag_loader_uploaded = true;
  flag_loader_translated = true;

  preselectedFile: ObjectStatus | null = null;
  isTranslateDisabled = true;

  constructor(private apiService: ApiService, private loadService: LoadService) { }

  ngOnInit(): void {
    this.loadSharedFiles();
    this.loadUploadedFiles();
    this.loadTranslatedFiles();
  }

  ngAfterViewInit() {
    this.sharedFilesDataSource.sort = this.sharedFilesSort;
    this.uploadedFilesDataSource.sort = this.uploadedFilesSort;
    this.translatedFilesDataSource.sort = this.translatedFilesSort;
  }

  ngOnDestroy() {
    if (this.sharedFilesSubscription) this.sharedFilesSubscription.unsubscribe();
    if (this.uploadedFilesSubscription) this.uploadedFilesSubscription.unsubscribe();
    if (this.translatedFilesSubscription) this.translatedFilesSubscription.unsubscribe();
  }

  API_TIMEOUT = 10000; // 10 seconds

  loadSharedFiles() {
    this.sharedFilesSubscription = this.apiService.getDownloadableFiles().pipe(
      catchError(error => {
        this.flag_loader_shared = false;
        this.sharedFilesDataSource.data = []; // Ensure data source is empty on error
        return of([]);
      })
    ).subscribe(
      (result: ObjectStatus[]) => {
        this.sharedFiles = result.filter(file => file.upload_state !== 'REMOVED');
        this.sharedFilesDataSource.data = this.sharedFiles;
        this.flag_loader_shared = false;

        // Refresh data if any file's malware scan is still in progress
        const needsScanRefresh = this.sharedFiles.some(file => file.malware_scan_status === 'IN_PROGRESS');
        if (needsScanRefresh) {
          setTimeout(() => this.loadSharedFiles(), 5000);
        }
      },
      (error: any) => {
        this.flag_loader_shared = false; // Ensure flag is set to false on error
      }
    );
  }

  loadUploadedFiles() {
    this.uploadedFilesSubscription = this.apiService.getUploadedFiles().pipe(
      catchError(error => {
        this.flag_loader_uploaded = false;
        this.uploadedFilesDataSource.data = []; // Ensure data source is empty on error
        return of([]);
      })
    ).subscribe(
      (result: ObjectStatus[]) => {
        this.uploadedFiles = result.filter(file => file.upload_state !== 'REMOVED' && !file.source_object_key);
        this.uploadedFilesDataSource.data = this.uploadedFiles;
        this.flag_loader_uploaded = false;

        // Refresh data if any file's malware scan is still in progress
        const needsScanRefresh = this.uploadedFiles.some(file => file.malware_scan_status === 'IN_PROGRESS');
        if (needsScanRefresh) {
          setTimeout(() => this.loadUploadedFiles(), 5000);
        }
      },
      (error: any) => {
        this.flag_loader_uploaded = false; // Ensure flag is set to false on error
      }
    );
  }

  loadTranslatedFiles() {
    this.translatedFilesSubscription = this.apiService.getTranslationStatus().pipe(
      catchError(error => {
        this.flag_loader_translated = false;
        this.translatedFilesDataSource.data = []; // Ensure data source is empty on error
        return of([]);
      })
    ).subscribe(
      (result: ObjectStatus[]) => {
        console.log(result);
        this.translatedFiles = result.filter(file => file.upload_state !== 'REMOVED');
        this.translatedFilesDataSource.data = this.translatedFiles;
        this.flag_loader_translated = false;

        // Refresh data if any element's translation is still in progress, or malware scan is in progress
        const needsRefresh = this.translatedFiles.some(
          file =>
            (file.upload_state !== 'SUCCESS' && file.upload_state !== 'REMOVED' && file.upload_state !== 'ERROR') ||
            file.malware_scan_status === 'IN_PROGRESS'
        );
        if (needsRefresh) {
          setTimeout(() => this.loadTranslatedFiles(), 5000); // Refresh every 5 seconds
        }
      },
      (error: any) => {
        this.flag_loader_translated = false; // Ensure flag is set to false on error
      }
    );
  }

  translateFiles(): void {
    const requests = this.selectedFiles
      .filter(file => file.file_size !== null && file.file_size <= 41943040) // Filter files larger than 40MB
      .map(file => {
        const body = {
          object_key: file.object_key,
          target_lang: this.selectedTargetLanguage // Send only the language code
        };
        return { request: this.apiService.translateFile(body), file };
      });

    requests.forEach(({ request, file }) => {
      // Create a fake result
      const fakeResult: ObjectStatus = {
        ...file,
        upload_state: 'TRANSLATION_INITIALIZED',
        object_key: "N/A",
        file_size: null,
        // Add the target language before extension to the file name
        file_name: `${file.file_name.split('.').slice(0, -1).join('.')}_${this.selectedTargetLanguage}.${file.file_name.split('.').pop()}`,
        // Today's date UTC
        upload_date: new Date().toUTCString(),
        // Todays date + 14 days UTC
        deletion_date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toUTCString(),
      };
      this.translatedFiles.push(fakeResult);
      this.translatedFilesDataSource.data = [...this.translatedFiles];

      request.subscribe(
        (result: ObjectStatus) => {
          console.log(result);
          // Replace the fake result with the actual result
          const index = this.translatedFiles.indexOf(fakeResult);
          if (index > -1) {
            this.translatedFiles[index] = result;
            this.translatedFilesDataSource.data = [...this.translatedFiles];
          }
          this.loadTranslatedFiles();
        },
        (error: any) => {
          // Handle error
        }
      );
    });

    // Unset checkboxes and selected languages
    this.selectedFiles = [];
    this.selectedTargetLanguage = '';
    this.isTranslateDisabled = true;
  }

  toggleFileSelection(file: ObjectStatus, isPreselected: boolean = false) {
    const index = this.selectedFiles.indexOf(file);
    if (index > -1) {
      if (!isPreselected) {
        this.selectedFiles.splice(index, 1);
      }
    } else {
      this.selectedFiles.push(file);
    }
    this.isTranslateDisabled = this.selectedFiles.length === 0;
  }

  downloadTranslatedFile(objectKey: string, fileName: string): void {
    this.apiService.getDownloadUrl(objectKey).subscribe((response: any) => {
      this.loadService.download(response.download_url, fileName);
    });
  }

  isFileTypeSupported(fileName: string): boolean {
    const extension = fileName.split('.').pop()?.toLowerCase();
    return this.fileTypes.includes(extension || '');
  }

  /**
   * Returns true if the file's malware scan status is not OK (i.e., IN_PROGRESS, ERROR, or THREAT)
   */
  isScanBlocking(file: ObjectStatus): boolean {
    return (
      file.malware_scan_status == null ||
      file.malware_scan_status === 'IN_PROGRESS' ||
      file.malware_scan_status === 'ERROR' ||
      file.malware_scan_status === 'THREAT'
    );
  }

  /**
   * Returns true if the row should be disabled (file type not supported, too large, or scan blocks)
   */
  isRowDisabled(file: ObjectStatus): boolean {
    return (
      !this.isFileTypeSupported(file.file_name) ||
      (file.file_size != null && file.file_size > 41943040) ||
      this.isScanBlocking(file)
    );
  }

  /**
   * Returns a tooltip message explaining why a row is disabled (all causes, newline separated)
   */
  getRowDisabledTooltip(file: ObjectStatus): string {
    const reasons: string[] = [];
    if (!this.isFileTypeSupported(file.file_name)) {
      reasons.push('File type not supported for translation.');
    }
    if (file.file_size != null && file.file_size > 41943040) {
      reasons.push('File size exceeds 40 MB limit.');
    }
    if (this.isScanBlocking(file)) {
      if (file.malware_scan_status === 'IN_PROGRESS') {
        reasons.push('Malware scan in progress. Actions are disabled until scan completes.');
      }
      if (file.malware_scan_status === 'THREAT') {
        reasons.push('Threat detected in file. Actions are disabled.');
      }
      if (file.malware_scan_status === 'ERROR') {
        reasons.push('Malware scan error. Actions are disabled.');
      }
    }
    return reasons.join('\n');
  }
}
